import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronRight, CircleDashed, Info, Loader2, Play, Square, Upload, Zap } from "lucide-react";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "./ConfirmDialog";
import SectionReviewPane from "./SectionReviewPane";
import {
  VerityApiError,
  extractRules,
  loadProgress,
  publishRuleSet,
  startProcessing,
  stopProcessing,
} from "./api";
import {
  ProgressSnapshot,
  ReviewState,
  SectionReviewState,
  StructureAssessment,
  VerityStandard,
  VeritySection,
} from "./types";

interface RequirementsTabProps {
  standard: VerityStandard;
  sections: VeritySection[];
  review: ReviewState;
  onReload: () => Promise<void>;
  /**
   * Reading the requirements, not building them.
   *
   * Ingesting a standard — processing its sections, correcting what the extractor made of them,
   * publishing a rule set — is administration. Everyone else needs to see what the compliance
   * engine is checking against, which is reading. Controls that write are removed rather than
   * disabled: a row of greyed-out buttons on a screen you will never be allowed to use is clutter.
   */
  readOnly?: boolean;
}

/**
 * What a section's state means to somebody looking at it.
 *
 * The labels deliberately separate MACHINE states from HUMAN ones. "Not processed" and "Pending
 * extraction" both describe work the server still owes, and neither is ever phrased as reading,
 * because an earlier "Not read" was taken to mean somebody had to go and read the section. Only
 * "Possible gap" and "In review" ask anything of a person.
 */
const SECTION_BADGE: Record<
  SectionReviewState,
  { status: "pass" | "warning" | "fail" | "info"; label: string }
> = {
  "not-processed": { status: "info", label: "Not processed" },
  candidates: { status: "pass", label: "Source verified" },
  "in-review": { status: "warning", label: "In review" },
  reviewed: { status: "pass", label: "Human reviewed" },
  "no-requirements": { status: "pass", label: "No requirements" },
  "machine-cleared": { status: "pass", label: "No requirements" },
  "extraction-gap": { status: "warning", label: "Possible gap" },
  "extraction-failed": { status: "fail", label: "Could not be read" },
  "needs-reprocessing": { status: "info", label: "Pending extraction" },
};

/** How often to ask the server how far it has got, while a run is live. */
const POLL_MS = 4000;

/**
 * The standard's chapters, and how far reading has got.
 *
 * The principle this screen exists to serve: ingesting a standard should feel automatic. Somebody
 * uploads a document and it gets read — they are not the mechanism. What they are shown is progress,
 * and then the places where their judgement actually adds something. Re-processing a chapter stays
 * available because it is genuinely useful after a prompt change, but nobody should have to click
 * fourteen lightning bolts to get a standard into the library.
 */
const RequirementsTab = ({
  standard,
  sections,
  review,
  onReload,
  readOnly = false,
}: RequirementsTabProps) => {
  const [openChapter, setOpenChapter] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<VeritySection | null>(null);
  const [busyChapter, setBusyChapter] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  /** Set when the server held a run back because the section tree does not look believable. */
  const [structureBlock, setStructureBlock] = useState<StructureAssessment | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The cheap poll's answer, which supersedes the heavy payload while a run is live. */
  const [snapshot, setSnapshot] = useState<ProgressSnapshot | null>(null);
  const inFlight = useRef(false);

  const processing = snapshot?.processing ?? review.processing;
  const live = processing?.state === "processing";

  /**
   * Polls progress, never the library.
   *
   * Two rules, both learned the hard way. It asks for counts rather than rule bodies, and it never
   * starts a request while one is outstanding. The previous version did both wrong, put roughly
   * 23 MB/min through the same process that was doing the extracting, and measurably slowed the work
   * it was reporting on.
   */
  useEffect(() => {
    if (!live) return;
    let cancelled = false;

    const tick = async () => {
      if (inFlight.current || cancelled) return;
      inFlight.current = true;
      try {
        const next = await loadProgress(standard.id);
        if (cancelled) return;
        setSnapshot(next);
        // The heavy payload is fetched exactly once, when the work stops.
        if (next.processing?.state !== "processing") await onReload();
      } catch {
        // A dropped poll is not worth surfacing; the next one says the same thing.
      } finally {
        inFlight.current = false;
      }
    };

    void tick();
    const id = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [live, standard.id, onReload]);

  const reviewBySection = useMemo(
    () => new Map((snapshot?.sectionReviews ?? review.sectionReviews).map((r) => [r.sectionId, r])),
    [review.sectionReviews, snapshot],
  );

  /**
   * Per-section counts, taken from the cheap snapshot while a run is live and from the full payload
   * otherwise. Same shape either way, so nothing downstream knows which it is reading.
   */
  const rulesBySection = useMemo(() => {
    const map = new Map<string, { total: number; actionRequired: number; reviewRecommended: number }>();

    if (snapshot) {
      for (const [sectionId, counts] of Object.entries(snapshot.bySection)) map.set(sectionId, counts);
      return map;
    }

    for (const rule of review.rules) {
      if (rule.decision === "excluded") continue;
      const entry = map.get(rule.sectionId) ?? { total: 0, actionRequired: 0, reviewRecommended: 0 };
      entry.total++;
      if (!rule.decision) {
        if (rule.validation === "action-required") entry.actionRequired++;
        if (rule.validation === "review-recommended") entry.reviewRecommended++;
      }
      map.set(rule.sectionId, entry);
    }
    return map;
  }, [review.rules, snapshot]);

  const stateOf = (section: VeritySection): SectionReviewState => {
    const record = reviewBySection.get(section.sectionId);
    if (!record) {
      // No record, but requirements exist: this section was plainly read. Records written before the
      // two processing paths agreed are missing, and a section holding eleven requirements must never
      // be described as unprocessed.
      return (rulesBySection.get(section.sectionId)?.total ?? 0) > 0 ? "candidates" : "not-processed";
    }

    // A ZERO-RULE VERDICT IS ONLY BELIEVABLE WITH PROOF THE MODEL ANSWERED.
    //
    // "This section has obligation language and no requirements" and "this section has no
    // requirements at all" are both claims about what the document says. Neither can be made from a
    // call that never completed — and every such record written before extraction evidence existed
    // came from the run that filed 146 rate-limited sections as containing nothing. Chapter 5 is the
    // proof: all 12 of its gaps held requirements, 76 of them.
    const unproven =
      (record.state === "extraction-gap" || record.state === "machine-cleared") &&
      record.extraction?.completed !== true;
    return unproven ? "needs-reprocessing" : record.state;
  };

  /**
   * Three separate facts about a section, never added together.
   *
   * A REQUIREMENT with an extraction problem is action required — a specific rule, in hand, that
   * cannot be published until somebody settles it. A SECTION Verity suspects it read past is a
   * possible extraction gap — a question about something that may not exist. A SECTION that could not
   * be read is a failure — nobody's judgement is wanted, it simply has to be run again.
   *
   * These were previously summed into one "action required" count per chapter, which is how a
   * standard holding 13 action-required requirements displayed 30 of them against a single chapter.
   */
  const actionRequiredOf = (section: VeritySection) =>
    rulesBySection.get(section.sectionId)?.actionRequired ?? 0;
  const recommendedOf = (section: VeritySection) =>
    rulesBySection.get(section.sectionId)?.reviewRecommended ?? 0;
  const isGap = (section: VeritySection) => stateOf(section) === "extraction-gap";
  const isFailed = (section: VeritySection) => stateOf(section) === "extraction-failed";
  /** Not read, or read by a run whose answer we cannot vouch for. Same practical state: read it. */
  const isUnread = (section: VeritySection) =>
    stateOf(section) === "not-processed" || stateOf(section) === "needs-reprocessing";

  /**
   * A section's colour.
   *
   * Red is reserved for the two things that are definitely wrong: a requirement that cannot be
   * published, and a section that was never read. A possible gap is amber, because "possible" is what
   * it is — turning a chapter red over a suspicion buries the requirement that genuinely needs work.
   */
  const rollOf = (section: VeritySection): "grey" | "blue" | "green" | "amber" | "red" => {
    if (isFailed(section)) return "red";
    if (isUnread(section)) return live ? "blue" : "grey";
    if (actionRequiredOf(section) > 0) return "red";
    if (isGap(section) || recommendedOf(section) > 0) return "amber";
    return "green";
  };

  /** Clear = read, nothing unresolved, nothing suspected, nothing to re-run. */
  const isClear = (section: VeritySection) =>
    !isUnread(section) && !isGap(section) && !isFailed(section) && actionRequiredOf(section) === 0;

  const chapters = useMemo(() => {
    const roots = sections.filter((s) => s.depth === 1);
    return roots.map((root) => {
      const members = sections.filter(
        (s) => s.numbering === root.numbering || s.numbering.startsWith(`${root.numbering}.`),
      );
      return {
        root,
        members,
        clear: members.filter(isClear).length,
        rules: members.reduce((n, s) => n + (rulesBySection.get(s.sectionId)?.total ?? 0), 0),
        // Requirements. Sections. Sections. Three counts, three meanings, never one total.
        actionRequired: members.reduce((n, s) => n + actionRequiredOf(s), 0),
        recommended: members.reduce((n, s) => n + recommendedOf(s), 0),
        gaps: members.filter(isGap).length,
        failed: members.filter(isFailed).length,
        active: members.filter((s) => (processing?.activeSections ?? []).includes(s.numbering)).length,
        done: members.filter((s) => !isUnread(s)).length,
        read: members.every((s) => !isUnread(s)),
        started: members.some((s) => !isUnread(s)),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, reviewBySection, rulesBySection]);

  const totals = useMemo(() => {
    const shared = {
      clearSections: sections.filter(isClear).length,
      /** SECTIONS Verity suspects it read past. Not requirements, and never added to them. */
      gaps: sections.filter(isGap).length,
      /** SECTIONS that could not be read. A machine problem with a machine answer: run them again. */
      failedSections: sections.filter(isFailed).length,
      /** SECTIONS never read, or read by a run whose zero-rule answer carries no evidence. */
      unread: sections.filter(isUnread).length,
    };
    if (snapshot) {
      return {
        rules: snapshot.counts.rules,
        verified: snapshot.counts.verified,
        recommended: snapshot.counts.reviewRecommended,
        actionRequired: snapshot.counts.actionRequired,
        humanReviewed: snapshot.counts.humanReviewed,
        ...shared,
      };
    }
    const rows = review.rules.filter((r) => r.decision !== "excluded");
    return {
      rules: rows.length,
      verified: rows.filter((r) => r.validation === "verified").length,
      recommended: rows.filter((r) => r.validation === "review-recommended" && !r.decision).length,
      actionRequired: rows.filter((r) => r.validation === "action-required" && !r.decision).length,
      humanReviewed: rows.filter((r) => r.decision === "approved" || r.decision === "edited").length,
      ...shared,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.rules, sections, reviewBySection, rulesBySection, snapshot]);

  const beginProcessing = async (acknowledgeStructure = false) => {
    setStarting(true);
    setError(null);
    try {
      await startProcessing(standard.id, acknowledgeStructure);
      setStructureBlock(null);
      await onReload();
    } catch (err) {
      // A 409 carrying an assessment is the structure gate, not a failure — it wants a decision,
      // so it is surfaced as one rather than as a red error line.
      const blocked = err instanceof VerityApiError && err.status === 409 && err.structure;
      if (blocked) setStructureBlock(err.structure!);
      else setError(err instanceof Error ? err.message : "Could not start processing.");
    } finally {
      setStarting(false);
    }
  };

  /**
   * Stops a run in flight.
   *
   * Cooperative, so it is not instant: the section already with the model finishes and is stored.
   * That is the point — a stop keeps everything already paid for, and Resume costs only the rest.
   */
  const halt = async () => {
    setStopping(true);
    setError(null);
    try {
      await stopProcessing(standard.id);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not stop the run.");
    } finally {
      setStopping(false);
    }
  };

  /** Re-reading one chapter. Kept for debugging and deliberate re-runs, not for ordinary use. */
  const reprocessChapter = async (numbers: string[], key: string) => {
    setBusyChapter(key);
    setError(null);
    try {
      await extractRules(standard.id, numbers, true);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Re-processing failed.");
    } finally {
      setBusyChapter(null);
    }
  };

  const publish = async () => {
    setConfirmPublish(false);
    setPublishing(true);
    setError(null);
    try {
      await publishRuleSet(standard.id);
      await onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not publish.");
    } finally {
      setPublishing(false);
    }
  };

  if (openSection) {
    return (
      <SectionReviewPane
        standard={standard}
        section={openSection}
        sections={sections}
        review={review}
        onBack={() => setOpenSection(null)}
        onReload={onReload}
      />
    );
  }

  const neverRead = !processing && totals.rules === 0;

  return (
    <>
      {error && <div className="vr-form-error">{error}</div>}

      {/* Nothing read yet. One button, and then the machine does the mechanical part. */}
      {neverRead && readOnly && (
        <div className="vr-empty">
          This standard has not been read yet, so it has no requirements to show.
        </div>
      )}
      {neverRead && !readOnly && (
        <GlowCard className="vr-start" hover={false}>
          <h3>Read this standard</h3>
          <p>
            IQ Verity will work through all {sections.length} sections, extract the requirements it
            finds, and check each one against its source. You will be shown the ones that need
            judgement — not asked to approve the rest.
          </p>
          <button className="vr-btn vr-btn-glow" onClick={() => void beginProcessing()} disabled={starting}>
            {starting ? (
              <>
                <Loader2 size={14} className="vr-spin-inline" /> Starting…
              </>
            ) : (
              <>
                <Play size={14} /> Process entire standard
              </>
            )}
          </button>
          <span className="vr-field-hint">
            This runs on the server — you can leave this page and come back.
          </span>
        </GlowCard>
      )}

      {/* The structure gate. Not an error — a decision, so it reads as one and offers both ways out. */}
      {structureBlock && (
        <GlowCard className="vr-structure-block" hover={false}>
          <div className="vr-card-head">
            <AlertTriangle size={16} style={{ color: "hsl(var(--vr-warning))" }} />
            <h3>This structure does not look right</h3>
          </div>
          <p>
            Reading requirements has been held. Rule extraction only ever reads the section spans
            below it, so a wrong structure produces a rule set that misses most of the document —
            slowly, and without saying so.
          </p>
          <ul className="vr-structure-issues">
            {structureBlock.issues.map((issue) => (
              <li key={issue.code}>{issue.detail}</li>
            ))}
          </ul>
          <p className="vr-field-hint">
            {structureBlock.metrics.sections} sections · {structureBlock.metrics.chapters} top-level ·{" "}
            {structureBlock.metrics.textPages} pages of text ·{" "}
            {Math.round(structureBlock.metrics.coverage * 100)}% of the text inside a section
          </p>
          <div className="vr-finding-actions">
            <button className="vr-btn vr-btn-ghost" onClick={() => setStructureBlock(null)}>
              Check the structure first
            </button>
            <button
              className="vr-btn vr-btn-outline"
              onClick={() => void beginProcessing(true)}
              disabled={starting}
            >
              {starting ? <Loader2 size={13} className="vr-spin-inline" /> : <Play size={13} />}
              Read it anyway
            </button>
          </div>
        </GlowCard>
      )}

      {processing && (
        <GlowCard className="vr-progress-card" hover={false}>
          <div className="vr-progress-head">
            {live ? (
              <>
                <Loader2 size={16} className="vr-spin-inline" />
                <strong>
                  {processing.stopRequested ? "Stopping — finishing the current section…" : "Reading requirements…"}
                </strong>
              </>
            ) : processing.state === "stopped" ? (
              // Stopped is its own outcome. Reporting it as complete would claim the standard had
              // been read when a person deliberately cut it short.
              <>
                <Square size={16} style={{ color: "hsl(var(--vr-warning))" }} />
                <strong>
                  Stopped after {processing.sectionsDone ?? 0} of{" "}
                  {processing.sectionsTotal ?? sections.length} sections
                </strong>
              </>
            ) : processing.state === "failed" ? (
              <>
                <AlertTriangle size={16} style={{ color: "hsl(var(--vr-fail))" }} />
                <strong>Processing stopped</strong>
              </>
            ) : (processing.failedSections?.length ?? 0) > 0 ? (
              // A run that dropped work must never describe itself as finished. The first
              // full-standard run reported "All 299 sections read" having failed to read 146 of them.
              <>
                <AlertTriangle size={16} style={{ color: "hsl(var(--vr-fail))" }} />
                <strong>
                  Read {sections.length - processing.failedSections!.length} of {sections.length}{" "}
                  sections — {processing.failedSections!.length} could not be read
                </strong>
              </>
            ) : (
              <>
                <Check size={16} style={{ color: "hsl(var(--vr-pass))" }} />
                <strong>All {sections.length} sections read</strong>
              </>
            )}
            <span className="vr-cell-dim">
              {processing.sectionsDone ?? 0} / {processing.sectionsTotal ?? sections.length} sections ·{" "}
              {Math.round(
                ((processing.sectionsDone ?? 0) / Math.max(processing.sectionsTotal ?? 1, 1)) * 100,
              )}
              % · {totals.rules} requirements identified
              {live && (processing.activeSections?.length ?? 0) > 0 && (
                <> · reading §{processing.activeSections!.slice(0, 3).join(", §")}</>
              )}
            </span>

            {/* Stopping is cooperative, so the button reports "Stopping…" until the loop reaches its
                next section boundary. Nothing already read is lost, and Resume picks up the rest. */}
            {/* A reviewer cannot start a run, so the only run they could stop is somebody else's. */}
            {live && !readOnly && (
              <button
                className="vr-rowbtn vr-danger vr-stop-btn"
                onClick={() => void halt()}
                disabled={stopping || processing.stopRequested}
                title="Finish the section being read, then stop"
              >
                {stopping || processing.stopRequested ? (
                  <>
                    <Loader2 size={12} className="vr-spin-inline" /> Stopping…
                  </>
                ) : (
                  <>
                    <Square size={12} /> Stop Reading
                  </>
                )}
              </button>
            )}
          </div>

          <div className="vr-progress">
            <div
              className="vr-progress-bar"
              style={{
                width: `${Math.round(((processing.sectionsDone ?? 0) / Math.max(processing.sectionsTotal ?? 1, 1)) * 100)}%`,
              }}
            />
          </div>

          {processing.state === "complete" && processing.timing && (
            <span className="vr-field-hint">
              Read {processing.timing.sectionsTimed} sections in{" "}
              {(processing.timing.wallMs / 1000).toFixed(0)}s at concurrency{" "}
              {processing.timing.concurrency} · mean {(processing.timing.meanMs / 1000).toFixed(1)}s,
              median {(processing.timing.medianMs / 1000).toFixed(1)}s per section ·{" "}
              {(processing.timing.modelMs / 1000).toFixed(0)}s model,{" "}
              {(processing.timing.storageMs / 1000).toFixed(0)}s storage
              {processing.timing.cacheHits > 0 && <> · {processing.timing.cacheHits} already read</>}
              {(processing.timing.sweeps ?? 1) > 1 && <> · {processing.timing.sweeps} sweeps</>}
              {(processing.timing.retries ?? 0) > 0 && (
                <>
                  {" "}
                  · {processing.timing.retries} retries across {processing.timing.retriedSections}{" "}
                  rate-limited sections
                </>
              )}
            </span>
          )}

          {(processing.failedSections?.length ?? 0) > 0 && (
            <span className="vr-field-hint vr-bad">
              Could not be read: §{processing.failedSections!.slice(0, 12).join(", §")}
              {processing.failedSections!.length > 12 && ` and ${processing.failedSections!.length - 12} more`}
              . Nothing was extracted from these — Resume processing to read them again.
            </span>
          )}

          {processing.state === "failed" && processing.error && (
            <span className="vr-field-hint">
              {processing.error} Everything read before the failure has been kept — resume to continue.
            </span>
          )}
        </GlowCard>
      )}

      {totals.rules > 0 && (
        <div className="vr-grid vr-grid-4">
          <div className="vr-stat">
            <span className="vr-stat-value">{totals.rules}</span>
            <span className="vr-stat-label">Requirements identified</span>
          </div>
          <div className="vr-stat vr-stat-ok">
            <span className="vr-stat-value">{totals.verified}</span>
            <span className="vr-stat-label">Source verified</span>
          </div>
          <div className="vr-stat vr-stat-warn">
            <span className="vr-stat-value">{totals.recommended}</span>
            <span className="vr-stat-label">Review recommended</span>
          </div>
          <div className="vr-stat vr-stat-bad">
            <span className="vr-stat-value">{totals.actionRequired}</span>
            <span className="vr-stat-label">Action required</span>
          </div>
        </div>
      )}

      {/* Everything above this point is the frame — where you are, and how the standard is doing.
          Everything below it is the work, and only the work scrolls. */}
      <div className="vr-scroll-region">
      <GlowCard className="vr-table-card" hover={false}>
        <div className="vr-table-head">
          <div>
            <h3>Chapters</h3>
            {/* Sections here, requirements in the tiles above. The two are counted separately and
                labelled with the noun they count, so a section-level suspicion can never be read as
                a requirement that needs work. */}
            <div className="vr-cell-code" style={{ marginTop: ".25rem" }}>
              {totals.clearSections} of {sections.length} sections clear
              {totals.unread > 0 && (
                <>
                  {" · "}
                  <span className="vr-info">{totals.unread} pending extraction</span>
                </>
              )}
              {totals.gaps > 0 && (
                <>
                  {" · "}
                  <span className="vr-warn">
                    {totals.gaps} section{totals.gaps === 1 ? "" : "s"} with a possible extraction gap
                  </span>
                </>
              )}
              {totals.failedSections > 0 && (
                <>
                  {" · "}
                  <span className="vr-bad">
                    {totals.failedSections} section{totals.failedSections === 1 ? "" : "s"} could not
                    be read — re-run them
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Processing and publishing are how the library is BUILT, and building it is the
              administrator's. A reader gets the same chapters and the same requirements below. */}
          {!readOnly && (
            <div style={{ display: "flex", gap: ".5rem" }}>
              {!neverRead && (
                <button
                  className="vr-rowbtn"
                  onClick={() => void beginProcessing()}
                  disabled={starting || live}
                  title="Read any sections that have not been read yet"
                >
                  {starting || live ? <Loader2 size={12} className="vr-spin-inline" /> : <Play size={12} />}{" "}
                  Resume processing
                </button>
              )}
              <button
                className="vr-btn vr-btn-glow vr-btn-sm"
                onClick={() => setConfirmPublish(true)}
                disabled={totals.clearSections === 0 || publishing || live}
                title={totals.clearSections === 0 ? "Nothing has been read yet" : undefined}
              >
                {publishing ? (
                  <>
                    <Loader2 size={13} className="vr-spin-inline" /> Publishing…
                  </>
                ) : (
                  <>
                    <Upload size={13} /> Publish rule set
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="vr-chapters">
          {chapters.map(({ root, members, rules, actionRequired, recommended, gaps, failed, active, done, read, started }) => {
            const key = root.numbering;
            const open = openChapter === key;
            // Worst-of, on the same rule as a section: red for a requirement that cannot be published
            // or a section that was never read; amber for a suspicion or a recommendation.
            const roll =
              actionRequired > 0 || failed > 0
                ? "red"
                : !read
                  ? live
                    ? "blue"
                    : "grey"
                  : gaps > 0 || recommended > 0
                    ? "amber"
                    : "green";

            return (
              <div key={root.sectionId} className={`vr-chapter ${open ? "vr-open" : ""}`}>
                <div className="vr-chapter-head">
                  <button
                    className="vr-chapter-toggle"
                    onClick={() => setOpenChapter(open ? null : key)}
                    disabled={!started}
                  >
                    <span className={`vr-chapter-mark vr-roll-${roll}`}>
                      {roll === "red" ? (
                        <AlertTriangle size={12} />
                      ) : roll === "amber" ? (
                        <Info size={12} />
                      ) : roll === "green" ? (
                        <Check size={13} />
                      ) : (
                        <CircleDashed size={13} />
                      )}
                    </span>
                    <span className="vr-chapter-num">{root.numbering}</span>
                    <span className="vr-chapter-title">{root.title}</span>
                  </button>

                  {/* Every outcome named as itself. A chapter says how much it verified, then what
                      is outstanding and of what kind — requirements and sections never merged into
                      one figure, because they are not the same thing and cannot be actioned alike. */}
                  <span className="vr-chapter-meta">
                    {!started ? (
                      <span className="vr-cell-dim">{live ? "Queued" : "Not processed"}</span>
                    ) : !read && active > 0 ? (
                      <span className="vr-info">
                        Reading · {done}/{members.length}
                      </span>
                    ) : (
                      <>
                        <span className="vr-ok">{rules - actionRequired - recommended} source verified</span>
                        {actionRequired > 0 && (
                          <span className="vr-bad"> · {actionRequired} action required</span>
                        )}
                        {recommended > 0 && (
                          <span className="vr-warn"> · {recommended} review recommended</span>
                        )}
                        {gaps > 0 && (
                          <span className="vr-warn">
                            {" "}
                            · {gaps} possible extraction gap{gaps === 1 ? "" : "s"}
                          </span>
                        )}
                        {failed > 0 && (
                          <span className="vr-bad">
                            {" "}
                            · {failed} section{failed === 1 ? "" : "s"} could not be read
                          </span>
                        )}
                        {read && actionRequired + recommended + gaps + failed === 0 && (
                          <span className="vr-ok"> · Clear</span>
                        )}
                      </>
                    )}
                  </span>

                  {/* Re-reading a chapter throws its rules away and extracts them again — real
                      money and a real change to the library. That is building it, not reading it,
                      so it belongs with the administrator alongside Process and Publish. */}
                  {!readOnly && (
                    <button
                      className="vr-rowbtn vr-icononly"
                      onClick={() => void reprocessChapter(members.map((m) => m.numbering), key)}
                      disabled={busyChapter !== null || live}
                      title="Re-read this chapter from scratch"
                      aria-label="Re-process chapter"
                    >
                      {busyChapter === key ? (
                        <Loader2 size={13} className="vr-spin-inline" />
                      ) : (
                        <Zap size={13} />
                      )}
                    </button>
                  )}
                </div>

                {live && !read && (
                  <div className="vr-chapter-track">
                    {active > 0 || done > 0 ? (
                      <div
                        className="vr-chapter-fill"
                        style={{ width: `${Math.round((done / Math.max(members.length, 1)) * 100)}%` }}
                      />
                    ) : (
                      <div className="vr-chapter-fill vr-queued" />
                    )}
                  </div>
                )}

                {open && (
                  <div className="vr-chapter-body">
                    {members.map((section) => {
                      const badge = SECTION_BADGE[stateOf(section)];
                      const counts = rulesBySection.get(section.sectionId);
                      const sectionRoll = rollOf(section);
                      return (
                        <button
                          key={section.sectionId}
                          className={`vr-section-row vr-row-${sectionRoll}`}
                          onClick={() => setOpenSection(section)}
                        >
                          <span className="vr-tree-num">{section.numbering}</span>
                          <span className="vr-tree-title">{section.title}</span>
                          <span className="vr-cell-dim">
                            {counts?.total
                              ? `${counts.total} requirement${counts.total === 1 ? "" : "s"}`
                              : "—"}
                          </span>
                          {isFailed(section) ? (
                            <StatusBadge status="fail" label="Could not be read" />
                          ) : (counts?.actionRequired ?? 0) > 0 ? (
                            <StatusBadge
                              status="fail"
                              label={`${counts!.actionRequired} action required`}
                            />
                          ) : isGap(section) ? (
                            <StatusBadge status="warning" label="Possible extraction gap" />
                          ) : (counts?.reviewRecommended ?? 0) > 0 ? (
                            <StatusBadge
                              status="warning"
                              label={`${counts!.reviewRecommended} review recommended`}
                            />
                          ) : (
                            <StatusBadge status={badge.status} label={badge.label} />
                          )}
                          <ChevronRight size={13} className="vr-cell-dim" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </GlowCard>

      {review.ruleSets.length > 0 && (
        <GlowCard className="vr-table-card" hover={false}>
          <div className="vr-table-head">
            <h3>Published rule sets</h3>
          </div>
          <div className="vr-chapters">
            {review.ruleSets.map((set) => (
              <div key={set.id} className="vr-ruleset-row">
                <StatusBadge
                  status={set.coverage.complete ? "pass" : "warning"}
                  label={`v${set.version}`}
                />
                <span>
                  <strong>{set.ruleCount}</strong> requirements published
                </span>
                {/* Stated separately and never merged: a machine check and a human reading are
                    different claims, and only one of them is what "approved" usually means. */}
                <span className="vr-cell-dim">
                  {set.coverage.sourceVerified} source verified · {set.coverage.humanReviewed} human
                  reviewed
                  {set.coverage.excluded > 0 && <> · {set.coverage.excluded} excluded</>}
                  {set.coverage.unresolved > 0 && (
                    <> · {set.coverage.unresolved} unresolved, not published</>
                  )}
                </span>
                <span className="vr-cell-dim">
                  {set.coverage.complete
                    ? "Coverage: complete standard"
                    : `Coverage: ${set.coverage.reviewedSections} of ${set.coverage.totalSections} sections`}
                </span>
                <span className="vr-cell-code">{new Date(set.publishedAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </GlowCard>
      )}
      </div>

      {confirmPublish && (
        <ConfirmDialog
          title="Publish a rule set?"
          tone="warn"
          confirmLabel="Publish"
          onCancel={() => setConfirmPublish(false)}
          onConfirm={() => void publish()}
        >
          <p className="vr-confirm-lead">
            <strong>{standard.title}</strong> — {standard.edition}
          </p>
          <p>
            {totals.verified} source-verified and {totals.recommended} review-recommended requirements
            from {totals.clearSections} clear section{totals.clearSections === 1 ? "" : "s"} will be
            frozen into a new version, {totals.humanReviewed} of them read by a person.
          </p>
          {totals.actionRequired > 0 && (
            <p className="vr-confirm-stress">
              {totals.actionRequired} requirement{totals.actionRequired === 1 ? "" : "s"} with an unresolved
              extraction problem will NOT be published. An open question is not a requirement.
            </p>
          )}
          <p className="vr-confirm-calm">
            The set records how much of the standard it covers and how much of it a person read, so it
            can never imply more than was done.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};

export default RequirementsTab;
